/* eslint-disable no-param-reassign */
import angular from 'angular';
import ngRoute from 'angular-route';
import ngResource from 'angular-resource';
import vcRecaptcha from 'angular-recaptcha';
import uiBootstrap from 'angular-ui-bootstrap';
import angulartics from 'angulartics';
import angularticsGoogleAnalytics from 'angulartics-google-analytics';
import angularInView from 'angular-inview';
import axios from 'axios';
import ngMap from 'ngmap';
import ngRollbar from 'ng-rollbar';
import { react2angular } from 'react2angular';
import { Icon, Switch, Pagination } from 'components';
import { setReporterInstance } from './errorReporter';

import { NewPassword, NewPasswordSuccess } from './AuthComponents';
import { InternalRouter, ExternalRouter } from '../Router';
import OnboardingRouter from '../Router/onboarding';
import {
  ApprovalSubmittedPage,
  ErrorUnverifiedUserPage,
  ErrorListingNotFoundPage,
  ErrorDashboardPrivateBrowsingPage,
} from '../Router/pages';

import 'froala-editor/js/froala_editor.pkgd.min';
import 'angular-froala';
import 'angular-ui-sortable';
import 'angularjs-dropdown-multiselect';
import 'angular-bootstrap-toggle/dist/angular-bootstrap-toggle';

// Import all the angularjs modules
import './app.dependencies';

import setAxiosInterceptors from '../api/interceptors';

import {
  angularRouteViewComponent,
  externalAngularViewComponent,
} from './angularRouteView';

import compareTo from './caferealtor/compareTo.directive';
import ngEnter from './caferealtor/ngEnter.directive';
import NoMoreShowingsService from './caferealtor/NoMoreShowings.service';
import loadContacts from './caferealtor/loadContacts.factory';
import showStatus from './caferealtor/showStatus.factory';
import CancelRemainingAppointmentsController from './caferealtor/CancelRemainingAppointments.controller';
import availabilityStyle from './caferealtor/availabilityStyle.directive';
import serviceAppointments from './caferealtor/serviceAppointments.directive';
import DefaultController from './caferealtor/Default.controller';
import ExternalController from './caferealtor/External.controller';
import LoginRedirectController from './caferealtor/LoginRedirect.controller';
import LoginController from './caferealtor/Login.controller';
import responseObserver from './caferealtor/responseObserver.factory';
import LS from './caferealtor/LS.factory';

// @ts-expect-error no types
import run from './app.run';
import csrfTokenService from './helpers/csrfTokenService';

/**
 * Axios changed its buildURL in the latest version, which resulted in different handling of null and undefined values.
 * Copied their old implementation to maintain our existing behaviour.
 * We are expecting null parameters should be removed in the URL instead of having empty string.
 * e.g. { a: 1, b: null, c: undefined } -> `a=1` instead of `a=1&b=`
 */
axios.defaults.paramsSerializer = {
  serialize: function paramsSerializer(params) {
    const parts: string[] = [];

    function encode(val: string) {
      return encodeURIComponent(val)
        .replace(/%3A/gi, ':')
        .replace(/%24/g, '$')
        .replace(/%2C/gi, ',')
        .replace(/%20/g, '+')
        .replace(/%5B/gi, '[')
        .replace(/%5D/gi, ']');
    }

    function serialize(key: string, val: any) {
      if (val === null || typeof val === 'undefined') {
        return;
      }

      if (Array.isArray(val)) {
        key += '[]';
      } else {
        val = [val];
      }

      val.forEach((v: any) => {
        if (v instanceof Date) {
          v = v.toISOString();
        } else if (typeof v === 'object') {
          v = JSON.stringify(v);
        }
        parts.push(`${encode(key)}=${encode(v)}`);
      });
    }

    Object.entries(params).forEach(([key, val]) => serialize(key, val));

    return parts.join('&');
  },
};

axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

setAxiosInterceptors();

Reflect.defineProperty(Number.prototype, 'formatMoney', {
  value(c: todo, d: todo, t: todo) {
    let n = Number(this);
    c = Math.abs(c);
    // eslint-disable-next-line no-restricted-globals
    c = isNaN(c) ? 2 : c;
    d = d == null ? '.' : d;
    t = t == null ? ',' : t;
    const s = n < 0 ? '-' : '';
    const i = String(parseInt((n = Math.abs(n || 0).toFixed(c) as any), 10));
    let j = i.length;
    j = j > 3 ? j % 3 : 0;
    return (
      s +
      (j ? i.substr(0, j) + t : '') +
      i.substr(j).replace(/(\d{3})(?=\d)/g, `$1${t}`) +
      (c
        ? d +
          Math.abs(n - (i as any))
            .toFixed(c)
            .slice(2)
        : '')
    );
  },
});

// Fix for bootstrap modals preventing focus on chat.
// https://github.com/sweetalert2/sweetalert2/issues/374#issuecomment-357459577
// @ts-expect-error
($.fn.modal as any).Constructor.prototype.enforceFocus = () => null;

const dependencies = [
  'angularjs-dropdown-multiselect',
  'ui.sortable',
  'ui.toggle',
  'froala',
  ngRollbar,
  uiBootstrap,
  vcRecaptcha,
  ngRoute,
  ngResource,
  angulartics,
  angularInView,
  ngMap,
  'caferealtor.listing.network.publish',
  'caferealtor.listing.network.publish_confirm',
  'caferealtor.notifications',
  'caferealtor.reports.appointment',
  'caferealtor.listing.merge',
  'caferealtor.help',
  'caferealtor.offer.list',
  'caferealtor.offer.message',
  'bb.notifications',
  'bb.history',
  'bb.users',
  'bb.util',
  'bb.assets',
  'bb.listing',
  'bb.listing.open_houses',
  'bb.messaging',
  'bb.my_business',
  'bb.external',
  'bb.network',
  'bb.almanac',
  'bb.admin',
  'bb.answering',
  'bb.announcement',
  'bb.offer',
  'bb.appointmentbooking',
];

const getRollbarConfig = (
  environment: 'qa' | 'staging' | 'production' | 'env4',
  accessToken: string,
) => {
  const defaultConfig = {
    accessToken,
    captureUncaught: true,
    captureUnhandledRejection: true,
    payload: {
      environment: environment.toLowerCase(),
      client: {
        javascript: {
          source_map_enabled: true,
          code_version: process.env.APP_VERSION,
          guess_uncaught_frames: true,
        },
      },
    },
  };

  const configs = {
    qa: defaultConfig,
    staging: defaultConfig,
    env4: defaultConfig,
    production: defaultConfig,
  };

  return configs[environment];
};

// Declare app level module which depends on views, and components
angular
  .module('caferealtor', dependencies)
  .constant('_', window._)
  .value('froalaConfig', {
    height: 400,
    key: 'mPD4tC3B1F1G1I2B2sGXd1WWTDSGXYOUKh1KINLb1OG1g1H-8D1B3D3D2E6D1G2B4D4H4==',
    imageUploadURL: '/upload/froala',
    fileUploadURL: '/upload/froala',
    emoticonsUseImage: false,
  })

  .directive('compareTo', compareTo)
  .directive('ngEnter', ngEnter as any)
  .directive('availabilityStyle', availabilityStyle)
  .directive('serviceAppointments', serviceAppointments)

  .service('NoMoreShowingsService', NoMoreShowingsService)
  .factory('loadContacts', loadContacts)
  .factory('showStatus', showStatus)
  .factory('LS', LS)
  .factory('responseObserver', responseObserver)

  .controller(
    'CancelRemainingAppointmentsController',
    CancelRemainingAppointmentsController,
  )
  .controller('DefaultController', DefaultController)
  .controller('ExternalController', ExternalController)
  .controller('LoginRedirectController', LoginRedirectController)
  .controller('LoginController', LoginController)

  .component('angularRouteView', angularRouteViewComponent)
  .component('externalAngularView', externalAngularViewComponent)

  .component(
    'switch',
    react2angular(Switch, [
      'label',
      'required',
      'addonAfter',
      'className',
      'onClick',
      'checked',
      'disabled',
    ]),
  )

  .component(
    'antdPagination',
    react2angular(Pagination, [
      'total',
      'defaultPageSize',
      'defaultCurrent',
      'showSizeChanger',
      'current',
      'onChange',
    ]),
  )

  .component(
    'brokerbayIcon',
    react2angular(Icon, [
      'color',
      'className',
      'style',
      'type',
      'width',
      'height',
      'offset',
      'flipped',
    ]),
  )

  .component('bbNewPassword', react2angular(NewPassword))
  .component('bbNewPasswordSuccess', react2angular(NewPasswordSuccess))
  .component('bbApprovalSubmitted', react2angular(ApprovalSubmittedPage))

  .component('bbInternalRouter', react2angular(InternalRouter))
  .component('bbExternalRouter', react2angular(ExternalRouter))
  .component('bbOnboardingRouter', react2angular(OnboardingRouter))

  .component(
    'bbErrorUnverifiedUserPage',
    react2angular(ErrorUnverifiedUserPage, ['mls']),
  )
  .component(
    'bbErrorListingNotFoundPage',
    react2angular(ErrorListingNotFoundPage, ['mls', 'listingInfo']),
  )
  .component(
    'bbErrorDashboardPrivateBrowsingPage',
    react2angular(ErrorDashboardPrivateBrowsingPage),
  )

  .config([
    '$locationProvider',
    function ($locationProvider: ng.ILocationProvider) {
      $locationProvider.hashPrefix('');
    },
  ])
  .config([
    'RollbarProvider',
    async function (RollbarProvider: any) {
      const rollbarEnabled = process.env.ROLLBAR_ENABLED === 'true';
      const rollbarEnvironment = process.env.ROLLBAR_ENV as 'qa' | 'production';
      const rollbarAccessToken = process.env.ROLLBAR_ACCESS_TOKEN;
      if (rollbarEnabled) {
        RollbarProvider.init(
          getRollbarConfig(rollbarEnvironment, rollbarAccessToken),
        );
        // This step is required to use the Rollbar instance created by ng-rollbar in errorReporter
        if ((window as any).Rollbar) {
          setReporterInstance((window as any).Rollbar);
        }
      }
    },
  ])
  .config([
    '$httpProvider',
    function ($httpProvider: ng.IHttpProvider) {
      // initialize get if not there
      if (!$httpProvider.defaults.headers.get) {
        $httpProvider.defaults.headers.get = {};
      }

      // Answer edited to include suggestions from comments
      // because previous version of code introduced browser-related errors

      // disable IE ajax request caching
      $httpProvider.defaults.headers.get['If-Modified-Since'] =
        'Mon, 26 Jul 1997 05:00:00 GMT';
      // extra
      $httpProvider.defaults.headers.get['Cache-Control'] = 'no-cache';
      $httpProvider.defaults.headers.get.Pragma = 'no-cache';

      $httpProvider.defaults.headers.common = {
        'X-Requested-With': 'XMLHttpRequest',
        'x-csrf-token': localStorage.getItem('csrfToken'),
      };

      $httpProvider.interceptors.push(() => ({
        request: config =>
          csrfTokenService.getToken().then((token: string) => {
            config.headers ??= {};
            config.headers['x-csrf-token'] = token;
            return config;
          }),
      }));

      $httpProvider.interceptors.push('responseObserver');
    },
  ])
  .run(run);

// conditionally include Google

try {
  angular.module(angularticsGoogleAnalytics);

  angular.module('caferealtor').requires.push(angularticsGoogleAnalytics);
} catch (e) {
  swal({
    title: 'Ad Blocker Detected',
    text: 'We have detected you have an Ad Blocker enabled. Some parts of BrokerBay might not work as expected. For a better experience, please disable AdBlock.',
    html: true,
    type: 'warning' as any,
    showCancelButton: false,
    confirmButtonColor: '#62cb31',
    confirmButtonText: 'I Understand',
    closeButtonText: 'Cancel',
    closeOnConfirm: true,
  } as SweetAlert.Settings & SweetAlert.PromtModalSettings);

  console.log("Warning - couldn't enable analytics. AdBlocker might be on.");
}

csrfTokenService.initializeToken().then(() => {
  angular.bootstrap(document, ['caferealtor']);
  // @ts-expect-error no types
  import('./homer');
});
